import _ from 'lodash'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useGetAdminEstablishmentsQuery, useGetAdminStaffsQuery } from '@/core/services/admin'
import { useCreateWaitingRoomMutation } from '@/core/services/waiting-room'

import { WaitingRoomForm } from '@/features/waiting-room'

import { getApiErrorMessage } from '@/utils/errors'

export default function CreateWaitingRoomForm() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { doctors } = useGetAdminStaffsQuery({ filter: { status: 'MEDECIN' } }, { selectFromResult: ({ data }) => ({ doctors: data?.[0] ?? [] }) })
  const { data: establishments } = useGetAdminEstablishmentsQuery()
  const [createWaitingRoom, { isLoading: isCreating }] = useCreateWaitingRoomMutation()

  const doctorsData = useMemo(
    () =>
      doctors.map((doctor) => ({
        id: doctor._id,
        cps: doctor.cps || '',
        name: doctor.userInfo.name,
        surname: doctor.userInfo.surname,
        email: doctor.email,
        stripeActivated: doctor.stripeActivated || false,
      })),
    [doctors]
  )

  const establishmentsData = useMemo(
    () =>
      establishments?.map((establishment) => ({
        id: establishment._id,
        active: establishment.active || false,
        name: establishment.name,
        admin: establishment.admin ? `${establishment.admin.userInfo.name} ${establishment.admin.userInfo.surname}` : '',
      })),
    [establishments]
  )

  const initialValues = useMemo(
    () => ({
      avgWaitingTime: 15,
      description: '',
      doctors: [],
      establishments: [],
      isPrioritized: false,
      name: '',
      openingHours: _.range(7).map((day) => ({
        day,
        periods: [],
      })),
      specialities: [],
      price: 0,
      paymentReceiverId: undefined,
      allowPatientSelection: false,
      paymentDialogConfig: { enabled: false, showForRoles: [] },
      fairDistribution: { enabled: false, maxConcurrent: 1, cooldownSeconds: 60, strategy: 'cap' as const, offerTtlSeconds: 30, prominentOffer: false },
      readinessConfig: { enabled: false, prioritizeReady: false, checklistTemplateId: '' },
      nurseDoctorExclusions: { enabled: false, rules: [] },
      coPresenceConfig: { enabled: false, allowAssign: false },
    }),
    []
  )

  return (
    <WaitingRoomForm
      doctors={doctorsData}
      establishments={establishmentsData}
      initialValues={initialValues}
      isAdmin
      isSubmitting={isCreating}
      onSubmit={async (values) => {
        if (values.paymentReceiverId === '') values.paymentReceiverId = undefined
        if (values.readinessConfig.checklistTemplateId === '') values.readinessConfig.checklistTemplateId = undefined
        try {
          const waitingRoom = await createWaitingRoom({
            ...values,
            specialities: _.map(values.specialities, (speciality) => _.pick(speciality, ['code', 'name', 'selectByDefault'])),
          }).unwrap()
          enqueueSnackbar(t('notification.waitingRoom.create.success'), { variant: 'success' })
          navigate(`/super-admin/waiting-rooms/${waitingRoom._id}`)
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.create.error')), { variant: 'error' })
        }
      }}
    />
  )
}
